import React, { useEffect, useState } from "react";
import { Field, FormikProvider, useFormik } from "formik";
import * as Yup from "yup";
import {
  Button,
  Col,
  Form,
  Modal,
  OverlayTrigger,
  Row,
  Tooltip,
} from "react-bootstrap";
import { POST_MaterialCategoryInsert } from "../../../helpers/url_helper";
import { Spin } from "antd";
import { post } from "../../../helpers/api_helper";
import swal from "sweetalert";
import CustomInput from "../../../CustomControls/CustomInput";
import CustomTextArea from "../../../CustomControls/CustomTextArea";
import { capitalizeString, errorMessage } from "../CommonFunctions";

const QuickAddMaterialCategory = ({
  categoryType,
  showModal,
  setShowModal,
  categoryTypeName,
  fnCallback,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const AuthUser = JSON.parse(localStorage.getItem("userDetails"));

  const validationSchema = Yup.object().shape({
    CategoryName: Yup.string()
      .trim()
      .required(`${categoryTypeName} is required field`),
  });

  const formik = useFormik({
    validationSchema: validationSchema,
    onSubmit: (values, formikHelpers) =>
      handleValidSubmit(values, formikHelpers),
    initialValues: {
      MaterialCategoryIDEncrypted: "",
      CategoryType: categoryType,
      CategoryName: "",
      Description: "",
      ShortCode: "",
      DisposalDays: "",
      ReadingCount: "",
    },
    validateOnBlur: false,
  });

  const {
    values: {},
  } = formik;

  const handleValidSubmit = (values) => {
    if (
      categoryType == 2 &&
      (values?.ShortCode?.trim()?.toLowerCase() == "pi" ||
        values?.ShortCode?.trim()?.toLowerCase() == "e" ||
        values?.ShortCode?.trim()?.toLowerCase() == "null")
    ) {
      formik.setFieldError(
        "ShortCode",
        "'Pi', 'E' & 'NULL' can not be used as short code",
      );
      formik.setFieldTouched("ShortCode", true);
      return;
    }

    const AuthUser = JSON.parse(localStorage.getItem("userDetails"));
    setIsLoading(true);

    let payload = {
      CategoryType: categoryType,
      CategoryName: values.CategoryName.trim(),
      Description: values.Description.trim(),
      CompanyIDEncrypted: AuthUser?.CompanyIDEncrypt,
      CreatedByEncrypted: AuthUser?.ReferenceIDEncrypt
        ? AuthUser?.ReferenceIDEncrypt
        : "0",
      ModifiedByEncrypted: AuthUser?.ReferenceIDEncrypt
        ? AuthUser?.ReferenceIDEncrypt
        : "0",
      ShortCode: categoryType == 2 ? values.ShortCode.trim() : "",
      DisposalDays: values?.DisposalDays ? values?.DisposalDays : 0,
      ReadingCount: categoryType == 2 ? values.ReadingCount : 0,
    };

    post(POST_MaterialCategoryInsert, payload)
      .then((response) => {
        setIsLoading(true);
        if (response.IsSuccess) {
          fnCallback(
            response?.MaterialCategoryIDEncrypted,
            values.CategoryName.trim(),
          );
          formik.resetForm();
          setShowModal(false);
          setIsLoading(false);
        } else {
          errorMessage(response?.Message);
          setIsLoading(false);
        }
        setIsLoading(false);
      })
      .catch((error) => {
        console.log(error);
        setIsLoading(false);
      });
  };

  return (
    <Modal className="fade" show={showModal} onHide={setShowModal}>
      <Spin
        size="large"
        spinning={isLoading}
        tip={"Loading..."}
        style={{ position: "fixed" }}
      >
        <FormikProvider value={formik}>
          <Form onSubmit={formik.handleSubmit}>
            <Modal.Header>
              <Modal.Title>{categoryTypeName}</Modal.Title>
              <Button
                type="button"
                variant=""
                className="btn-close"
                onClick={() => {
                  formik.resetForm();
                  setShowModal(false);
                }}
              ></Button>
            </Modal.Header>
            <Modal.Body>
              <Row>
                <Col lg={12}>
                  <Field
                    type="text"
                    label={categoryTypeName}
                    name="CategoryName"
                    placeholder={`Enter ${categoryTypeName}`}
                    component={CustomInput}
                    maxLength={250}
                    required
                    autoFocus
                    onChange={(e) => {
                      formik.setFieldValue(
                        "CategoryName",
                        capitalizeString(e?.target?.value),
                      );
                    }}
                  />
                </Col>
                {categoryType == 2 && (
                  <Col lg={12}>
                    <label className="mb-1">
                      Short Code &nbsp;
                      <OverlayTrigger
                        trigger={["hover", "focus"]}
                        responsive={true}
                        overlay={
                          <Tooltip className="toltip-popover">
                            <strong className="p-1">
                              {" "}
                              Short code to be used in formula. It should be
                              unique. Constant like 'Pi', 'E' & 'NULL' can not
                              be used as short code.
                            </strong>
                          </Tooltip>
                        }
                      >
                        <span>
                          <i
                            className="fa fa-info-circle"
                            style={{ cursor: "pointer", color: "#9698ab" }}
                          ></i>
                        </span>
                      </OverlayTrigger>
                    </label>
                    <Field
                      name="ShortCode"
                      placeholder={"Enter Short Code"}
                      component={CustomInput}
                      maxLength={50}
                      onChange={(e) => {
                        const inputValue = e?.target?.value ?? "";
                        formik.setFieldValue(
                          "ShortCode",
                          inputValue.replace(/\s+/g, ""),
                        ); //doesn't allow space
                      }}
                    />
                  </Col>
                )}
                {categoryType == 1 &&
                  AuthUser?.Permissions?.FoodTesting == 1 && (
                    <Col lg={12}>
                      <Field
                        type="number"
                        label={"Disposal Days"}
                        name="DisposalDays"
                        placeholder={"Enter Disposal Days"}
                        component={CustomInput}
                        numberTypeWithoutDot={true}
                      />
                    </Col>
                  )}
                {categoryType == 2 ? (
                  <Col lg={12}>
                    <Field
                      label="Reading Count"
                      type="number"
                      name="ReadingCount"
                      placeholder={"Enter Reading Count"}
                      component={CustomInput}
                      numberTypeWithoutDot={true}
                      onChange={(e) => {
                        const inputValue = e?.target?.value ?? "";
                        formik.setFieldValue("ReadingCount", inputValue ?? "");
                      }}
                    />
                  </Col>
                ) : (
                  ""
                )}
                <Col lg={12}>
                  <Field
                    type="text"
                    label="Description"
                    name="Description"
                    placeholder={"Enter Description"}
                    component={CustomTextArea}
                    maxLength={500}
                    rows={5}
                  />
                </Col>
              </Row>
            </Modal.Body>
            <Modal.Footer>
              <Button
                //  variant="primary"
                variant="primary btn-sm"
                type="submit"
              >
                <i className="fa-solid fa-floppy-disk"></i> Submit
              </Button>
              <Button
                // variant="danger light"
                variant="danger btn-sm"
                onClick={() => {
                  formik.resetForm();
                  setShowModal(false);
                }}
              >
                <i className="fa fa-cancel"></i> Cancel
              </Button>
            </Modal.Footer>
          </Form>
        </FormikProvider>
      </Spin>
    </Modal>
  );
};

export default QuickAddMaterialCategory;
